import { hasOpenRiserSides } from '../../../../../config/blissStairSides.js'
import type { Bed } from './beds'
import { measure, type Room, type Wall } from './model.js'
import { BRIDGE, BRIDGE_DEPTH, COMPACT_BRIDGE, bridgeWall } from './bridge-layout.js'

export const CUSTOM_STYLES = ['bridge', 'rosemary', 'divider', 'inlet', 'watercolor', 'scenic'] as const
export type CustomStyle = (typeof CUSTOM_STYLES)[number]
// Stored style IDs predate the corrected owner-supplied names; retain them for saved plans.
export const CUSTOM_OPTIONS: {
  id: CustomStyle
  name: string
  reference: string
  description: string
}[] = [
  {
    id: 'bridge',
    name: 'Perpendicular bridge',
    reference: 'Bridge reference',
    description: 'Two Twin uppers + two Full lowers · shared stairs · horizontal guards',
  },
  {
    id: 'rosemary',
    name: 'Double perpendicular · central stairs',
    reference: 'Scenic 30A',
    description: 'Two Twin uppers + two Full lowers · X-pattern guards · enclosed stairs',
  },
  {
    id: 'divider',
    name: 'Double parallel · adjustable center divider',
    reference: 'Floor-to-ceiling divider concept',
    description: 'Two upper + two lower beds · adjustable center divider · outside ladders',
  },
  {
    id: 'inlet',
    name: 'Paired parallel · ladders',
    reference: 'Inlet-inspired',
    description: 'Four Twin beds · two ladder-access stacks · central aisle',
  },
  {
    id: 'watercolor',
    name: 'Single perpendicular · side stairs',
    reference: 'Watercolor-inspired',
    description: 'One Twin upper + one Full lower · X-pattern guards · paneled enclosure',
  },
  {
    id: 'scenic',
    name: 'Floating parallel with stairs',
    reference: '10–11 ft room concept',
    description:
      'One Twin upper + one Full lower · widening storage stairs · ideal for 10–11 ft spans',
  },
]
export const isCustomLayout = (b: Pick<Bed, 'kind'>) => b.kind === 'bridge' || b.kind === 'custom'
export const hasCustomLayout = (beds: Bed[]) => beds.some(isCustomLayout)
export const customStyle = (b: Pick<Bed, 'kind' | 'customStyle'>): CustomStyle =>
  b.kind === 'bridge' ? 'bridge' : (b.customStyle ?? 'bridge')
export const customOption = (b: Pick<Bed, 'kind' | 'customStyle'>) =>
  CUSTOM_OPTIONS.find(o => o.id === customStyle(b))!
export const CUSTOM = {
  side: 3,
  clearStair: 24,
  approach: 36,
  headroom: 30,
  frameDepth: 12,
  mattress: 6,
  lowerDeck: 12,
  ladderDepth: 4,
} as const
export type BedRect = {
  x: number
  y: number
  w: number
  h: number
  axis: 'x' | 'z'
  head: 'left' | 'right' | 'back'
  size: 'Twin' | 'Twin XL' | 'Full' | 'Queen'
}
export type Rect = { x: number; y: number; w: number; h: number }
export function hasCompactStairs(b: Pick<Bed, 'kind' | 'customStyle'>) {
  return ['bridge', 'rosemary'].includes(customStyle(b))
}
export function hasCompactStringerStairs(b: Pick<Bed, 'kind' | 'customStyle' | 'stairStyle'>) {
  return b.stairStyle === 'stringer' && hasCompactStairs(b)
}
/** Optional frame depth is local to this paired design. */
export function customFrameDepth(b: Pick<Bed, 'kind' | 'customStyle' | 'upperFrameDepth'>) {
  return hasCompactStairs(b) && Number.isFinite(b.upperFrameDepth) && b.upperFrameDepth! >= 6 && b.upperFrameDepth! <= 24 ? b.upperFrameDepth! : CUSTOM.frameDepth
}
/** Per-design upper assembly offsets; omitted values preserve existing models. */
export function customUpperOffsets(b: Pick<Bed, 'kind' | 'customStyle' | 'upperFrameRise' | 'upperMattressLift'>) {
  const valid = (n: number | undefined) => hasCompactStairs(b) && Number.isFinite(n) && n! >= 0 && n! <= 12 ? n! : 0
  return { frameRise: valid(b.upperFrameRise), mattressLift: valid(b.upperMattressLift) }
}
/** Vertical dimensions shared by every stair style in the paired layouts, in inches. */
export function customVerticalProfile(b: Pick<Bed, 'kind' | 'customStyle' | 'stairStyle'> & Partial<Pick<Bed, 'height' | 'designerDimensions'>>) {
  if (b.designerDimensions && Number.isFinite(b.height)) return { deckHeight: b.height! - 30, height: b.height!, risers: hasCompactStairs(b) ? 6 : BRIDGE.risers }
  return hasCompactStairs(b)
    ? { deckHeight: 72, height: 102, risers: 6 }
    : { deckHeight: BRIDGE.deckHeight, height: BRIDGE.height, risers: BRIDGE.risers }
}
type StairExperiment = Pick<Bed, 'kind' | 'customStyle' | 'stairStyle'> & Partial<Pick<Bed, 'height' | 'designerDimensions' | 'stairRise' | 'stairRiserCount'>>
/** Keep the stair landing separate from the upper bunk's fixed deck elevation. */
export function customStairProfile(b: StairExperiment) {
  const upper = customVerticalProfile(b), compact = hasCompactStairs(b)
  const risers = compact && Number.isInteger(b.stairRiserCount) && b.stairRiserCount! >= 2 && b.stairRiserCount! <= 24
    ? b.stairRiserCount! : upper.risers
  const rise = compact && Number.isFinite(b.stairRise) && b.stairRise! >= 1 && b.stairRise! <= 16
    ? b.stairRise! : upper.deckHeight / upper.risers
  return {risers, rise, treads: risers - 1, landingHeight: rise * risers, upperDeckHeight: upper.deckHeight}
}
export const hasStairDimensions = (b: Pick<Bed, 'stairLandingDepth' | 'stairTreadRun' | 'stairRise' | 'stairRiserCount'>) =>
  [b.stairLandingDepth, b.stairTreadRun, b.stairRise, b.stairRiserCount].some(value => value !== undefined)
export function customGeometry(b: Pick<Bed, 'width' | 'kind' | 'customStyle' | 'stairStyle' | 'mattressConfig' | 'lowerBedSize' | 'upperBedSize' | 'ladderWidth' | 'dividerWidth' | 'lowerLeftOffset' | 'lowerRightOffset'> & Partial<Pick<Bed, 'depth' | 'designerDimensions' | 'stairLandingDepth' | 'stairTreadRun' | 'stairRise' | 'stairRiserCount'>>) {
  const style = customStyle(b),
    paired = style === 'bridge' || style === 'rosemary' || style === 'divider'
  let uppers: BedRect[],
    lowers: BedRect[],
    landing: Rect | null = null,
    stairs: Rect | null = null,
    divider: Rect | null = null,
    ladders: Rect[] = []
  if (paired) {
    const xl = style === 'divider' ? b.upperBedSize === 'twinxl' : b.mattressConfig === 'twinxl_queen',
      length = xl ? 87 : 82,
      lowerWidth = style === 'divider' && b.lowerBedSize === 'twin' ? 44 : xl ? 66 : 60,
      ladderWidth = style === 'divider' ? Math.max(12, Math.min(18, b.ladderWidth ?? 18)) : 18,
      requestedDivider = style === 'divider' ? Math.max(0, b.dividerWidth ?? b.width - 2 * length) : 0,
      dividerWidth = style === 'divider' ? Math.min(Math.max(0, b.width - 2 * length), requestedDivider) : 0,
      cabinGapShift = style === 'divider' ? (b.width - 2 * length - dividerWidth) / 2 : 0,
      leftUpperX = style === 'divider' ? cabinGapShift : 0,
      rightUpperX = style === 'divider' ? b.width - length - cabinGapShift : b.width - length
    uppers = [
      { x: leftUpperX, y: 0, w: length, h: 44, axis: 'x', head: 'left', size: xl ? 'Twin XL' : 'Twin' },
      { x: rightUpperX, y: 0, w: length, h: 44, axis: 'x', head: 'right', size: xl ? 'Twin XL' : 'Twin' },
    ]
    lowers = [
      { x: Math.max(0, Math.min(b.width - lowerWidth, b.lowerLeftOffset ?? (style === 'rosemary' ? length - lowerWidth : 0))), y: 0, w: lowerWidth, h: length, axis: 'z', head: 'back', size: style === 'divider' && b.lowerBedSize === 'twin' ? (xl ? 'Twin XL' : 'Twin') : xl ? 'Queen' : 'Full' },
      { x: Math.max(0, Math.min(b.width - lowerWidth, b.width - lowerWidth + (b.lowerRightOffset ?? (style === 'rosemary' ? lowerWidth - length : 0)))), y: 0, w: lowerWidth, h: length, axis: 'z', head: 'back', size: style === 'divider' && b.lowerBedSize === 'twin' ? (xl ? 'Twin XL' : 'Twin') : xl ? 'Queen' : 'Full' },
    ]
    if (style === 'divider') {
      // The center partition is the intentional variable: it absorbs the room-width remainder
      // while mattress/frame dimensions stay fixed. It runs floor-to-ceiling in the 3D model.
      divider = { x: leftUpperX + length, y: 0, w: dividerWidth, h: 44 }
      // Ladders sit on the extreme OUTER vertical faces of the two cabins.
      // Their plan footprint projects in front of the cabin; it must not consume
      // any of the open floor span between the perpendicular lower beds.
      // Outside ladders must clear the perpendicular lower beds. Put each ladder
      // on the upper cabin's far OUTER end, beyond the lower-bed footprint.
      // In local plan coordinates the upper runs on X, so the ladder is a narrow
      // vertical strip at the extreme outer end of each upper, not across the lower bed.
      ladders = [
        // 18-inch ladders at the two far OUTER cabin ends. They occupy the
        // front/outer edge lanes, clear of the perpendicular lower mattresses.
        { x: leftUpperX, y: 44, w: ladderWidth, h: 4 },
        { x: rightUpperX + length - ladderWidth, y: 44, w: ladderWidth, h: 4 },
      ]
    } else {
      const landingDepth = Number.isFinite(b.stairLandingDepth) && b.stairLandingDepth! > 0
        ? b.stairLandingDepth! : COMPACT_BRIDGE.landingDepth
      const treadRun = Number.isFinite(b.stairTreadRun) && b.stairTreadRun! > 0
        ? b.stairTreadRun! : COMPACT_BRIDGE.stairRun / 5
      const stairRun = treadRun * customStairProfile(b).treads
      landing = { x: length, y: 0, w: b.width - 2 * length, h: landingDepth }
      stairs = { x: length, y: landingDepth, w: b.width - 2 * length, h: stairRun }
    }
  } else if (style === 'inlet') {
    uppers = [
      { x: 0, y: 0, w: 44, h: 82, axis: 'z', head: 'back', size: 'Twin' },
      { x: b.width - 44, y: 0, w: 44, h: 82, axis: 'z', head: 'back', size: 'Twin' },
    ]
    lowers = uppers.map(r => ({ ...r }))
    ladders = uppers.map(r => ({ x: r.x + 10, y: 82, w: 24, h: 4 }))
  } else {
    uppers = [{ x: 0, y: 0, w: 82, h: 44, axis: 'x', head: 'left', size: 'Twin' }]
    lowers =
      style === 'watercolor'
        ? [{ x: 3, y: 0, w: 60, h: 82, axis: 'z', head: 'back', size: 'Full' }]
        : [{ x: 0, y: 0, w: 82, h: 60, axis: 'x', head: 'left', size: 'Full' }]
    const stairWidth = style === 'scenic' ? b.width - 82 : 30
    landing = { x: 82, y: 0, w: stairWidth, h: 44 }
    stairs = { x: 82, y: 44, w: stairWidth, h: 71.25 }
  }
  if (b.designerDimensions && b.depth !== undefined) {
    const base = customSize(style, b.width, b.stairStyle, b.mattressConfig).depth
    const extra = Math.max(0, b.depth - base)
    // Extend the approach/flight, keeping every mattress its selected size.
    if (stairs && b.stairTreadRun === undefined) stairs = {...stairs, h: stairs.h + extra}
    if (extra) lowers = lowers.map(rect => ({...rect, y: rect.y + extra}))
  }
  return { uppers, lowers, landing, stairs, divider, ladders }
}
export function customStairMetrics(b: Bed) {
  const profile = customStairProfile(b), g = customGeometry(b)
  const flight = g.stairs?.h ?? 0, run = flight / profile.treads
  const reach = g.stairs ? g.stairs.y + flight : 0
  const bedFront = Math.min(...g.lowers.map(rect => rect.y + rect.h))
  return {...profile, run, flight, reach, bedFront, landingDepth: g.landing?.h ?? 0,
    angle: Math.atan2(profile.rise, run) * 180 / Math.PI,
    landingOffset: profile.landingHeight - profile.upperDeckHeight,
    setback: bedFront - reach}
}
/** Floor-post footprints shared by the preview and bedside clearance checks. */
export function customStringerSupportPosts(b: Pick<Bed, 'width' | 'kind' | 'customStyle' | 'stairStyle' | 'mattressConfig'>): Rect[] {
  const g = customGeometry(b), l = g.landing
  if (b.stairStyle !== 'stringer' || !l) return []
  const slimFrontPost = hasCompactStringerStairs(b)
  const postWidth = slimFrontPost ? 1.5 : 3.5
  return [
    {
      x: slimFrontPost ? g.uppers[0].x + g.uppers[0].w - postWidth : l.x,
      y: slimFrontPost ? g.uppers[0].y + g.uppers[0].h - 3.5 : l.y + l.h - 3.5,
      w: postWidth,
      h: 3.5,
    },
    {
      x: slimFrontPost ? g.uppers[1].x : l.x + l.w - postWidth,
      // Place the slim post beneath the fascia, flush with its inner and front faces.
      y: slimFrontPost ? g.uppers[1].y + g.uppers[1].h - 3.5 : l.y + l.h - 3.5,
      w: postWidth,
      h: 3.5,
    },
  ]
}
export function customSize(style: CustomStyle, wallSpan: number, stairStyle?: Bed['stairStyle'], mattressConfig?: Bed['mattressConfig']) {
  const xl = mattressConfig === 'twinxl_queen' && ['bridge', 'rosemary', 'divider'].includes(style)
  const minWidth = style === 'bridge' || style === 'rosemary' ? (xl ? 204 : 194) : style === 'divider' ? 154 : style === 'inlet' ? 124 : 112
  return {
    width:
      style === 'bridge' || style === 'rosemary' || style === 'divider' || style === 'inlet' || style === 'scenic'
        ? Math.max(minWidth, wallSpan)
        : minWidth,
    depth:
      style === 'inlet'
        ? 86
        : ['bridge', 'rosemary', 'divider'].includes(style)
          ? (xl ? 87 : COMPACT_BRIDGE.depth)
          : BRIDGE_DEPTH,
    height: customVerticalProfile({ kind: style === 'bridge' ? 'bridge' : 'custom', customStyle: style === 'bridge' ? undefined : style, stairStyle }).height,
    minWidth,
  }
}
export function fitCustomToRoom(room: Room, b: Bed, resetPosition = false): Bed {
  const vertical = b.rotation % 180 === 90,
    defaults = customSize(customStyle(b), vertical ? room.depth : room.width, b.stairStyle, b.mattressConfig),
    size = b.designerDimensions ? { ...defaults, width: Math.max(defaults.minWidth, b.width), depth: Math.max(defaults.depth, b.depth), height: b.height } : defaults
  // A shallower flight must keep the same supporting-wall position on walls B/C.
  const x = b.x + (b.rotation === 270 ? b.depth - size.depth : 0),
    y = b.y + (b.rotation === 180 ? b.depth - size.depth : 0)
  return {
    ...b,
    name: CUSTOM_OPTIONS.find(o => o.id === customStyle(b))!.name,
    width: size.width,
    depth: size.depth,
    height: size.height,
    x: resetPosition
      ? b.rotation === 270
        ? Math.max(0, room.width - size.depth)
        : 0
      : Math.max(0, Math.min(x, room.width - (vertical ? size.depth : size.width))),
    y: resetPosition
      ? b.rotation === 180
        ? Math.max(0, room.depth - size.depth)
        : 0
      : Math.max(0, Math.min(y, room.depth - (vertical ? size.width : size.depth))),
  }
}
export function createCustomLayout(room: Room, wall: Wall, style: CustomStyle, id: string): Bed {
  return fitCustomToRoom(
    room,
    {
      id,
      kind: style === 'bridge' ? 'bridge' : 'custom',
      ...(style !== 'bridge' ? { customStyle: style } : {}),
      name: CUSTOM_OPTIONS.find(o => o.id === style)!.name,
      x: 0,
      y: 0,
      width: 194,
      depth: BRIDGE_DEPTH,
      height: BRIDGE.height,
      ...(style === 'divider' ? { upperBedSize: 'twin' as const, lowerBedSize: 'full' as const, ladderWidth: 18, dividerWidth: Math.max(0, room.width - 164), lowerLeftOffset: 0, lowerRightOffset: 0 } : {}),
      rotation: ({ A: 0, B: 270, C: 180, D: 90 } as const)[wall],
    },
    true
  )
}
export function customFitIssues(
  room: Room,
  wall: Wall,
  style: CustomStyle,
  stairStyle?: Bed['stairStyle'],
  mattressConfig?: Bed['mattressConfig']
): string[] {
  const span = wall === 'A' || wall === 'C' ? room.width : room.depth,
    depth = wall === 'A' || wall === 'C' ? room.depth : room.width,
    size = customSize(style, span, stairStyle, mattressConfig),
    issues: string[] = []
  if (span < size.minWidth)
    issues.push(
      `Wall ${wall} needs at least ${measure(size.minWidth)} for these example bed sizes and access.`
    )
  if (depth < size.depth + CUSTOM.approach)
    issues.push(
      `Room depth needs at least ${measure(size.depth + CUSTOM.approach)}, including ${measure(CUSTOM.approach)} in front for access.`
    )
  const headroom = room.height - customVerticalProfile({ kind: style === 'bridge' ? 'bridge' : 'custom', customStyle: style === 'bridge' ? undefined : style, stairStyle }).deckHeight - CUSTOM.mattress
  if (headroom < CUSTOM.headroom)
    issues.push(
      `Only ${measure(headroom)} above the upper mattress; this example uses a ${measure(CUSTOM.headroom)} planning allowance.`
    )
  return issues
}
/** Cabinet follows the clear gap, never stretches into either mattress or access lane. */
export function cabinBookshelf(b: Bed): (Rect & { height: number }) | null {
  if (!isCustomLayout(b) || customStyle(b) !== 'divider') return null
  const { lowers } = customGeometry(b)
  const [left, right] = [...lowers].sort((a, z) => a.x - z.x)
  if (!left || !right) return null
  const gap = right.x - left.x - left.w
  // One inch each side allows the existing projecting bed trim to remain clear.
  const width = Math.min(36, Math.floor((gap - 2) * 2) / 2)
  if (!Number.isFinite(width) || width < (b.bookshelfStyle === 'recessed_arch' ? 28 : 18)) return null
  return { x: left.x + left.w + (gap - width) / 2, y: 0, w: width, h: 12, height: customVerticalProfile(b).deckHeight - CUSTOM.frameDepth }
}
export function cabinBookshelfDescription(b: Bed) {
  if (!b.builtInBookshelf) return ''
  const shelf = cabinBookshelf(b)
  if (b.bookshelfStyle === 'recessed_arch') return shelf ? `Recessed arch library: ${shelf.w}″ W · 12″ maximum cabinet depth · two sleeper-facing arched niches · curved overhead sweep to upper-bunk front · matches frame finish · estimated in package pricing` : 'Recessed arch library selected · needs at least 30″ between lower beds · adjust layout before quoting'
  return shelf
    ? `Built-in bookshelf: ${shelf.w}″ W × ${shelf.h}″ D × ${shelf.height}″ H · three open compartments · matches frame finish · estimated in package pricing`
    : 'Built-in bookshelf selected · needs at least 20″ between lower beds · adjust layout before quoting'
}
export function customBedCount(b: Bed) {
  return customGeometry(b).uppers.length * 2
}
export function customCapacity(b: Bed) {
  const g = customGeometry(b)
  return [...g.uppers, ...g.lowers].reduce((n, r) => n + (r.size.startsWith('Twin') ? 1 : 2), 0)
}
export function customStairWidth(b: Bed) {
  return customGeometry(b).stairs?.w ?? customGeometry(b).divider?.w ?? 0
}
export function customClearWidth(b: Bed) {
  const g = customGeometry(b)
  return g.stairs
    ? g.stairs.w - (b.stairStyle && b.stairStyle !== 'enclosed' ? 0 : 2 * CUSTOM.side)
    : customStyle(b) === 'divider' ? (g.divider?.w ?? 0) : b.width - 88
}
export function customDescription(b: Bed) {
  const original = customOption(b)
  const option = {...original, description: b.mattressConfig === 'twinxl_queen' ? original.description.replace('Twin uppers', 'Twin XL uppers').replace('Full lowers', 'Queen lowers') : original.description}
  return (
    (b.stairStyle === 'stringer'
      ? option.description.replace(
          /enclosed stairs|shared stairs|widening storage stairs/g,
          'stairs'
        ) + ' · exposed stringers, open risers and support posts; no handrails or stair drawers'
      : b.stairStyle === 'open'
        ? option.description.replace(/enclosed stairs/g, 'stairs') +
          ' · open sides without handrails'
        : hasOpenRiserSides(b) ? option.description.replace(/enclosed stairs/g, 'open-riser stairs') + ' · shortened sides, level top caps and open rear landing bay for a shared nightstand' : option.description) +
    (customGeometry(b).stairs
      ? ` · stairs ${measure(customStairWidth(b))} overall / ${measure(customClearWidth(b))} clear`
      : customStyle(b) === 'divider'
        ? ` · center divider ${measure(customClearWidth(b))} wide · floor-to-ceiling; ceiling/truss attachment requires field framing verification`
        : ` · central aisle ${measure(customClearWidth(b))}`) +
    (b.lowerShiplap ? ' · U-shaped lower shiplap enclosure · back and outer returns to ladder rear edge · matches frame finish · estimated in package pricing' : '') +
    (b.builtInBookshelf ? ` · ${cabinBookshelfDescription(b)}` : '') +
    (b.designerDimensions
      ? ` · designer dimensions ${measure(b.width)} × ${measure(b.depth)} × ${measure(b.height)} · upper deck ${measure(customVerticalProfile(b).deckHeight)}` : '') +
    (hasCompactStairs(b)
      ? ` · ${Number(customStairMetrics(b).rise.toFixed(2))}″ rise × ${Number(customStairMetrics(b).run.toFixed(2))}″ run · ${customStairMetrics(b).risers} rises · landing ${measure(customStairMetrics(b).landingHeight)} high · upper deck ${measure(customStairMetrics(b).upperDeckHeight)} · ${customStairMetrics(b).landingDepth}″ landing depth, ${customStairMetrics(b).flight}″ flight`
      : '')
  )
}
export function customAccessWarnings(room: Room, b: Bed) {
  const style = customStyle(b),
    issues = customFitIssues(room, bridgeWall(b.rotation), style, b.stairStyle, b.mattressConfig)
  if (b.designerDimensions) {
    const defaultHeadroom = issues.findIndex(issue => issue.includes('above the upper mattress'))
    if (defaultHeadroom >= 0) issues.splice(defaultHeadroom, 1)
    const headroom = room.height - customVerticalProfile(b).deckHeight - CUSTOM.mattress
    if (headroom < CUSTOM.headroom) issues.push(`Only ${measure(headroom)} above the upper mattress; this example uses a ${measure(CUSTOM.headroom)} planning allowance.`)
  }
  if (hasCompactStairs(b)) {
    const stair = customStairMetrics(b)
    if (Math.abs(stair.landingOffset) > 0.01) issues.push(`the stair landing is ${measure(Math.abs(stair.landingOffset))} ${stair.landingOffset < 0 ? 'below' : 'above'} the upper-bunk deck. Review this connection or match the landing to the upper deck.`)
    if (stair.landingHeight >= room.height) issues.push('the stair landing reaches or exceeds the room ceiling. Reduce the rise or number of rises.')
  }
  const backGap =
    b.rotation === 0
      ? b.y
      : b.rotation === 90
        ? b.x
        : b.rotation === 180
          ? room.depth - b.y - b.depth
          : room.width - b.x - b.depth
  const frontGap =
    b.rotation === 0
      ? room.depth - b.y - Math.max(b.depth, hasCompactStairs(b) ? customStairMetrics(b).reach : b.depth)
      : b.rotation === 90
        ? room.width - b.x - Math.max(b.depth, hasCompactStairs(b) ? customStairMetrics(b).reach : b.depth)
        : b.rotation === 180
          ? b.y
          : b.x
  if (style !== 'scenic' && backGap > 0.5)
    issues.push(
      'moved away from its supporting wall. Return it to the wall or have your designer plan the support.'
    )
  if (b.builtInBookshelf && !cabinBookshelf(b)) issues.push(`the ${b.bookshelfStyle === 'recessed_arch' ? 'recessed arch library needs at least 30' : 'built-in bookshelf needs at least 20'}″ between the lower beds. Move them apart or remove the bookshelf.`)
  if (frontGap < CUSTOM.approach && !issues.some(s => s.includes('in front for access')))
    issues.push(
      `Keep ${measure(CUSTOM.approach)} in front of the access route; only ${measure(Math.max(0, frontGap))} remains here.`
    )
  return issues
}











